import { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';
import { Auth0FrontendUser, Auth0OrganizationData, Auth0OrganizationID, IHttpResponse } from '@gr/shared/models';
import { isPreProdOrg } from '@gr/shared/utils';
import { configure } from 'axios-hooks';
import { secondsToMilliseconds } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import "react-datetime/css/react-datetime.css";
import { Route, Switch } from 'react-router-dom';
import AppWrapper from './AppWrapper';
import { TokenResponse, axiosGet, configureAxiosWithAuth0 } from './authAxios';
import Login from './components/Login';
import { PageNotFound } from './components/shared/Auth/PageNotFound';
import { useMembership } from './contexts/MembershipContext';
import { initializeWebSocket } from './contexts/SocketContext';
import { useAuth0UserData } from './hooks/useAuth0Custom';
import { Auth0FrontendConfig } from './providers/auth0-data.provider';
import { getProductAuth0OrgId } from './providers/auth0-product.provider';
import { endSession } from './providers/auth0-recovery.provider';
import { Auth0LocalStorage, getCachedOrganizations, getCachedSelectedOrgName, setCachedOrganizations, setCachedSelectedOrgName } from './providers/auth0-storage.provider';
import { AUTH0_AUDIENCE, createAuth0Scope } from './providers/auth0.service';

export interface IAppProps {
  auth0Config: Auth0FrontendConfig;
}

const App = ({
  auth0Config,
}: IAppProps): JSX.Element => {
  const {
    isAuthenticated: isLoggedIn,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    logout: auth0Logout,
    user: auth0User,
    loginWithRedirect,
  } = useAuth0UserData();

  const { setMembership, setMembershipLoaded, isMembershipLoaded, auth0LocalStorage, setOrganizations, organizationsReady, setOrganizationsReady } = useMembership();

  const [isConfigured, setIsConfigured] = useState(false);
  const [organizationsFetched, setOrganizationsFetched] = useState(false);

  const hasEndedSession = useRef(false);
  const lastTokenRefreshRef = useRef<number>(0);

  function getOrgSession(auth0LocalStorage: Auth0LocalStorage | undefined, orgId: Auth0OrganizationID) {
    return auth0LocalStorage
      ?.sessions
      ?.find(session => session?.user?.org_id === orgId);
  }

  const getUserOrganizations = async (auth0User: Auth0FrontendUser): Promise<Auth0OrganizationData[]> => {
    const hasAllP2PTenants = auth0User?.app_metadata?.hasAllP2PTenants ?? false;
    const isLocal = window.location.origin.includes('localhost');

    let organizations: Auth0OrganizationData[] = [];

    if (hasAllP2PTenants) {
      // First try to get from cache
      const cachedOrganizations = getCachedOrganizations();

      if (cachedOrganizations && cachedOrganizations.length > 0) {
        organizations = cachedOrganizations;
      } else {
        // If cache is empty, fetch from API
        try {
          const result = await axiosGet<IHttpResponse<Auth0OrganizationData[]>>('/auth0-organizations');
          organizations = result?.data?.data ?? [];
        } catch (error) {
          console.error('Error fetching organizations:', error);

          // fallback to auth0User.organizations
          organizations = auth0User?.organizations ?? [];
        }
      }
    } else {
      organizations = auth0User?.organizations ?? [];
    }

    return isLocal
      ? organizations?.filter(org => isPreProdOrg(org)) ?? []
      : organizations ?? [];
  };

  useEffect(() => {
    if (isLoggedIn && auth0User && auth0LocalStorage && auth0Config && !hasEndedSession.current) {
      const p2pSession = getOrgSession(auth0LocalStorage, getProductAuth0OrgId());
      const cachedUserId = p2pSession?.user?.user_id;

      // A missing id is a cold start awaiting repopulation — only a session provably
      // belonging to a DIFFERENT user may end this one (removeP2pSession() deletes the
      // very key this check reads, so acting on an absent id loops the user out)
      const isDifferentUser = !isNil(cachedUserId) && !isNil(auth0User?.user_id) && cachedUserId !== auth0User.user_id;

      if (isDifferentUser) {
        console.warn('[App] Cached p2p session belongs to a different user, ending session', {
          cachedUserId,
          currentUserId: auth0User.user_id,
        });

        hasEndedSession.current = true;

        endSession({
          auth0Config,
          auth0Logout,
          loginWithRedirect,
          redirectOptions: {
            authorizationParams: {
              organization: auth0Config.organizationId,
              audience: AUTH0_AUDIENCE,
              scope: createAuth0Scope({ orgName: auth0Config.organization }),
            },
          },
        }).catch(() => {
          hasEndedSession.current = false;
        });
      }
    }
  }, [isLoggedIn, auth0User?.user_id, auth0LocalStorage, auth0Config]);

  // Reset organizations fetched flag when user or config changes
  useEffect(() => {
    setOrganizationsFetched(false);
    setOrganizationsReady(false);
    setMembershipLoaded(false); // Reset membership so it gets set again with proper timing
  }, [auth0User?.user_id, auth0Config?.organizationId]);

  useEffect(() => {
    if (isConfigured) {
      return;
    }

    if (isLoggedIn) {
      const auth = async () => {
        try {
          await getAccessToken();
        } catch (e) {
          console.error(e);
        }
      };

      if (isLoggedIn) {
        auth();
      }

      initializeWebSocket(auth0Config);
    }
  }, [isLoggedIn, auth0Config, isConfigured]);

  useEffect(() => {
    if (isLoggedIn && !!auth0Config && !!auth0User && !isMembershipLoaded) {
      // For users with hasAllP2PTenants, wait until organizations are ready before setting membership
      const hasAllP2PTenants = auth0User?.app_metadata?.hasAllP2PTenants ?? false;

      if (hasAllP2PTenants && !organizationsReady) {
        // Wait for organizations to be fetched
        return;
      }

      setMembership({
        organization: auth0Config?.organization,
        roles: [auth0User?.auth_roles?.[0]],
      });

      const cachedOrgName = getCachedSelectedOrgName();

      if (isEmpty(cachedOrgName) || cachedOrgName !== auth0Config?.organization) {
        setCachedSelectedOrgName(auth0Config?.organization);
      }

      setMembershipLoaded(true);
    }
  }, [isLoggedIn, auth0Config, auth0User, isMembershipLoaded, organizationsReady]);

  // Fetch organizations after axios is configured for users with hasAllP2PTenants
  useEffect(() => {
    if (isConfigured && isLoggedIn && auth0User && auth0User?.app_metadata?.hasAllP2PTenants && !organizationsFetched) {
      const fetchOrganizations = async () => {
        try {
          const organizations = await getUserOrganizations(auth0User);
          setOrganizations(organizations);
          setCachedOrganizations(organizations); // Cache for AppMain.tsx
          setOrganizationsFetched(true);
          setOrganizationsReady(true);
        } catch (error) {
          console.error('Error setting organizations:', error);
          // Even on error, set organizations ready to prevent infinite blocking
          setOrganizationsReady(true);
        }
      };

      fetchOrganizations();
    }
  }, [isConfigured, isLoggedIn, auth0User?.user_id, auth0User?.app_metadata?.hasAllP2PTenants, organizationsFetched]);

  // Set organizations ready immediately for users who don't have hasAllP2PTenants
  useEffect(() => {
    if (isConfigured && isLoggedIn && auth0User && !auth0User?.app_metadata?.hasAllP2PTenants) {
      // For regular users, convert their organizations from auth0User to the expected format
      const userOrganizations = auth0User?.organizations ?? [];
      setOrganizations(userOrganizations);
      setOrganizationsReady(true);
    }
  }, [isConfigured, isLoggedIn, auth0User?.user_id, auth0User?.app_metadata?.hasAllP2PTenants]);

  async function getAccessToken() {
    if (!auth0Config) {
      return;
    }

    let response: GetTokenSilentlyVerboseResponse | undefined = undefined;

    try {
      response = await getAccessTokenSilently({
        cacheMode: 'on',
        detailedResponse: true,
        authorizationParams: {
          organization: auth0Config.organizationId,
          audience: AUTH0_AUDIENCE,
          scope: createAuth0Scope({ orgName: auth0Config.organization }),
        }
      });
    } catch (error) {
      await loginWithRedirect({
        authorizationParams: {
          organization: auth0Config.organizationId,
          audience: AUTH0_AUDIENCE,
          scope: createAuth0Scope({ orgName: auth0Config.organization }),
        }
      });
    }

    if (response) {
      const token: TokenResponse = {
        id_token: response.id_token,
        access_token: response.access_token,
        expires_in: response.expires_in,
        scope: response.scope,
      };

      await configureAxios(token);
    }
  }

  const configureAxios = useCallback(async (token: TokenResponse) => {
    if (!auth0Config) {
      return;
    }

    const authAxios = configureAxiosWithAuth0({
      auth0Config,
      token,
      auth0User,
      auth0Logout,
      getAccessTokenSilently,
      loginWithRedirect,
    });

    // Swaps the instance axios-hooks hands to subsequent useAxios calls so they carry the new token.
    configure({
      axios: authAxios,
      defaultOptions: { useCache: false },
    });

    setIsConfigured(true);
  }, [auth0Config, auth0User, auth0Logout, getAccessTokenSilently, loginWithRedirect]);

  // Mint a fresh access token so a tab left open past the token lifetime doesn't 401 into a forced
  // sign-out. Deliberately does NOT call loginWithRedirect on failure, unlike getAccessToken's
  // cold-start path: a network blip on focus would then eject every user to sign-in. Best-effort —
  // a genuinely dead session is still caught by the 401 path in authAxios.
  const refreshAuthToken = useCallback(async () => {
    if (!auth0Config) {
      return;
    }

    try {
      const response = await getAccessTokenSilently({
        cacheMode: 'off',
        detailedResponse: true,
        authorizationParams: {
          organization: auth0Config.organizationId,
          audience: AUTH0_AUDIENCE,
          scope: createAuth0Scope({ orgName: auth0Config.organization }),
        },
      });

      const token: TokenResponse = {
        id_token: response.id_token,
        access_token: response.access_token,
        expires_in: response.expires_in,
        scope: response.scope,
      };

      await configureAxios(token);
    } catch (error) {
      console.error('[App]: Failed to refresh auth token on focus', error);
    }
  }, [auth0Config, getAccessTokenSilently, configureAxios]);

  // useAuth0UserData rebuilds auth0User every render, so refreshAuthToken is new every render too.
  // A ref keeps the listener effect below off that identity instead of re-subscribing each render.
  const refreshAuthTokenRef = useRef(refreshAuthToken);

  useEffect(() => {
    refreshAuthTokenRef.current = refreshAuthToken;
  }, [refreshAuthToken]);

  // Refresh when the user returns to the tab, throttled so rapid tab switching doesn't hammer Auth0.
  useEffect(() => {
    if (!isConfigured || !isLoggedIn || !auth0Config) {
      return;
    }

    const minRefreshIntervalMs = secondsToMilliseconds(30);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const now = performance.now();
      if (now - lastTokenRefreshRef.current < minRefreshIntervalMs) {
        return;
      }

      lastTokenRefreshRef.current = now;
      refreshAuthTokenRef.current();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isConfigured, isLoggedIn, auth0Config]);

  // Determine if the app is fully ready for API calls
  const isAppReady = isConfigured && organizationsReady;

  return (
    <Switch>
      <Route path="/app" render={(props) => <AppWrapper configured={isAppReady} auth0Config={auth0Config} />} />
      <Route
        exact
        path="/"
        render={(props) => (
          <Login
            auth0Config={auth0Config}
          />
        )}
      />
      <Route
        exact
        path="/login"
        render={(props) => (
          <Login
            auth0Config={auth0Config}
          />
        )}
      />
      <Route component={PageNotFound} />
    </Switch>
  );
};

export default App;
